// Data loader for the internal /intern/produktion page (publicly accessible,
// kept unlisted rather than password-gated). Mirrors the PHP implementation
// on midtfynsfestival.dk (includes/production.php) and consumes the same
// production.json contract published by the BackOffice.

import { DATA_BASE_URL, sanitizeHtml } from './data.ts';
import type {
  ProductionDataRaw,
  ProductionFileRaw,
  ProductionFileType,
  ProductionFileView,
  ProductionStageRaw,
  ProductionStageView,
  ProductionView,
  ProductionWelcomeView,
} from './types.ts';

/** Allow http(s)/mailto/tel and scheme-less URLs; reject anything else (javascript: etc.). */
export function sanitizeUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed === '') return '';
  const schemeMatch = /^([a-z][a-z0-9+.-]*):/i.exec(trimmed);
  if (schemeMatch && !/^(https?|mailto|tel)$/i.test(schemeMatch[1] ?? '')) return '';
  return trimmed;
}

/** Danish-aware slugify: æ→ae, ø→oe, å→aa, everything else collapsed to '-'. */
export function productionSlugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Human-readable file size, e.g. 482133 → "471 KB" (Danish decimal comma). */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  const power = Math.max(0, Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1));
  const size = bytes / 1024 ** power;
  const decimals = power === 0 || size >= 100 ? 0 : 1;
  return `${size.toFixed(decimals).replace('.', ',')} ${units[power] ?? 'B'}`;
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg']);

/** Normalise a single raw file entry; returns null when it has no usable URL. */
export function productionFileView(raw: ProductionFileRaw | null | undefined): ProductionFileView | null {
  if (!raw || typeof raw !== 'object') return null;

  const url = sanitizeUrl(String(raw.url ?? ''));
  if (url === '') return null;

  const fileName = String(raw.fileName ?? '').trim();
  const title = String(raw.title ?? '').trim() || fileName || 'Download';

  let fileType = String(raw.fileType ?? '').trim().toLowerCase() as ProductionFileType | string;
  if (fileType !== 'pdf' && fileType !== 'image') {
    const ext = (fileName || url).split('.').pop()?.toLowerCase() ?? '';
    fileType = ext === 'pdf' ? 'pdf' : IMAGE_EXTENSIONS.has(ext) ? 'image' : 'file';
  }

  return {
    title,
    fileName,
    url,
    fileType: fileType as ProductionFileType,
    isImage: fileType === 'image',
    sizeLabel: formatFileSize(Number(raw.sizeBytes ?? 0)),
  };
}

const DEFAULT_WELCOME_BODY =
  '<p>Her finder du produktionsinformation og downloads for festivalens scener. ' +
  'Vælg en scene i fanerne ovenfor.</p>';

/** Normalise the raw production.json payload into render-ready data. */
export function normalizeProduction(raw: unknown): ProductionView {
  const data = (raw && typeof raw === 'object' ? raw : {}) as ProductionDataRaw;

  const welcome: ProductionWelcomeView = {
    title: String(data.welcome?.title ?? '').trim() || 'Velkommen',
    bodyHtml: sanitizeHtml(data.welcome?.bodyHtml ?? '') || DEFAULT_WELCOME_BODY,
    imageUrl: sanitizeUrl(String(data.welcome?.imageUrl ?? '')),
  };

  const usedSlugs = new Set<string>();
  const stages: ProductionStageView[] = [];

  // Note: explicit annotations because Array.isArray() widens readonly arrays to any[].
  const stageList: readonly ProductionStageRaw[] = Array.isArray(data.stages) ? data.stages : [];

  stageList.forEach((stage, index) => {
    if (!stage || typeof stage !== 'object') return;

    const name = String(stage.name ?? '').trim();
    if (name === '') return;

    let slug =
      productionSlugify(String(stage.slug ?? '')) ||
      productionSlugify(name) ||
      `scene-${index + 1}`;
    const baseSlug = slug;
    let suffix = 2;
    while (usedSlugs.has(slug)) {
      slug = `${baseSlug}-${suffix}`;
      suffix += 1;
    }
    usedSlugs.add(slug);

    const rawFiles: readonly ProductionFileRaw[] = Array.isArray(stage.files) ? stage.files : [];
    const files = rawFiles
      .map(productionFileView)
      .filter((file): file is ProductionFileView => file !== null);

    stages.push({
      id: String(stage.id ?? ''),
      slug,
      name,
      displayOrder: Number(stage.displayOrder ?? index + 1),
      imageUrl: sanitizeUrl(String(stage.imageUrl ?? '')),
      descriptionHtml: sanitizeHtml(stage.descriptionHtml ?? ''),
      files,
    });
  });

  stages.sort(
    (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name, 'da'),
  );

  return { welcome, stages };
}

/** Fetch and normalise production.json from the configured data base (CDN in prod). */
export async function loadProduction(): Promise<ProductionView> {
  const res = await fetch(DATA_BASE_URL + 'production.json', { credentials: 'omit' });
  if (!res.ok) throw new Error(`Failed to load production.json (HTTP ${res.status})`);
  return normalizeProduction(await res.json());
}
